import { useState } from 'react';
import { router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import { AdminLayout } from '@/components/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable, type ColumnProps } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock, RotateCcw, UserMinus } from 'lucide-react';

const DEACTIVATION_REASON_LABELS: Record<string, string> = {
  mentee_unresponsive: 'Mentee unresponsive',
  no_longer_available: 'Mentor no longer available',
  other: 'Other',
  cycle_rollover: 'Cycle rollover',
  backfill: 'Before pool tracking',
};

interface PoolMentee {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  category: 'pre-health' | 'graduate' | 'fellowship';
  waiting_since: string;
  days_waiting: number;
  returned_to_pool: boolean;
}

interface InactiveMentee {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  category: 'pre-health' | 'graduate' | 'fellowship';
  deactivated_at: string | null;
  deactivation_reason: string | null;
}

interface Props {
  in_pool: PoolMentee[];
  inactive: InactiveMentee[];
  filters: { category: string; min_days: number };
  counts: { in_pool: number; inactive: number; in_pool_unfiltered: number };
}

export default function MenteePoolIndex({
  in_pool,
  inactive,
  filters,
  counts,
}: Props) {
  const [selected, setSelected] = useState<number[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const allSelected = in_pool.length > 0 && selected.length === in_pool.length;

  const toggleAll = () =>
    setSelected(allSelected ? [] : in_pool.map((mentee) => mentee.id));

  const toggleOne = (id: number) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    );

  // Both filters travel together, otherwise changing one silently resets the other.
  const applyFilters = (next: { category?: string; min_days?: number }) => {
    const category = next.category ?? filters.category;
    const minDays = next.min_days ?? filters.min_days;
    const query: Record<string, string | number> = {};
    if (category !== 'all') query.category = category;
    if (minDays > 0) query.min_days = minDays;

    setSelected([]);
    router.get('/admin/mentee_pool', query, { preserveState: true, replace: true });
  };

  const deactivateSelected = () => {
    setSubmitting(true);
    router.post(
      '/admin/mentee_pool/deactivate',
      { user_ids: selected },
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false);
          setConfirming(false);
          setSelected([]);
        },
      }
    );
  };

  const reactivate = (mentee: InactiveMentee) =>
    router.post(
      '/admin/mentee_pool/reactivate',
      { user_id: mentee.id },
      { preserveScroll: true }
    );

  const inactiveColumns: ColumnProps<InactiveMentee>[] = [
    {
      header: 'Name',
      render: (mentee) => (
        <div>
          <div className="font-medium">
            {mentee.first_name} {mentee.last_name}
          </div>
          <div className="text-sm text-muted-foreground">
            {mentee.email_address}
          </div>
        </div>
      ),
    },
    {
      header: 'Category',
      render: (mentee) => <Badge variant="outline">{mentee.category}</Badge>,
    },
    {
      header: 'Removed',
      render: (mentee) =>
        mentee.deactivated_at ? (
          <span className="text-sm text-muted-foreground">
            {formatDistanceToNow(new Date(mentee.deactivated_at), {
              addSuffix: true,
            })}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        ),
    },
    {
      header: 'Reason',
      render: (mentee) => (
        <span className="text-sm">
          {mentee.deactivation_reason
            ? DEACTIVATION_REASON_LABELS[mentee.deactivation_reason] ??
              mentee.deactivation_reason
            : '—'}
        </span>
      ),
    },
    {
      header: '',
      render: (mentee) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => reactivate(mentee)}
          className="gap-1"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reactivate
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout title="Mentee Pool">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-muted-foreground max-w-xl">
              Mentees in the pool are visible to mentors browsing for new
              mentees. Removing them from the pool emails them and hides them
              until you put them back.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Waiting over:</label>
              <Select
                value={String(filters.min_days)}
                onValueChange={(value) => applyFilters({ min_days: Number(value) })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Any</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="120">120 days</SelectItem>
                </SelectContent>
              </Select>

              <label className="text-sm font-medium ml-4">Category:</label>
              <Select
                value={filters.category}
                onValueChange={(value) => applyFilters({ category: value })}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pre-health">Pre-health</SelectItem>
                  <SelectItem value="graduate">Graduate</SelectItem>
                  <SelectItem value="fellowship">Fellowship</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs
            defaultValue="in_pool"
            onValueChange={() => setSelected([])}
          >
            <TabsList>
              <TabsTrigger value="in_pool">
                In Pool ({counts.in_pool})
              </TabsTrigger>
              <TabsTrigger value="inactive">
                Inactive ({counts.inactive})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="in_pool" className="mt-4">
              {filters.min_days > 0 && (
                <p className="mb-3 text-sm text-muted-foreground">
                  Showing {counts.in_pool} of {counts.in_pool_unfiltered} in the pool,
                  waiting more than {filters.min_days} days. Longest wait first.
                </p>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Select all mentees currently listed"
                        disabled={in_pool.length === 0}
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Waiting</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {in_pool.map((mentee) => (
                    <TableRow key={mentee.id}>
                      <TableCell>
                        <Checkbox
                          checked={selected.includes(mentee.id)}
                          onCheckedChange={() => toggleOne(mentee.id)}
                          aria-label={`Select ${mentee.first_name} ${mentee.last_name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {mentee.first_name} {mentee.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {mentee.email_address}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{mentee.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            mentee.days_waiting >= 60
                              ? 'text-sm font-medium text-amber-700 flex items-center gap-1'
                              : 'text-sm text-muted-foreground flex items-center gap-1'
                          }
                        >
                          {mentee.days_waiting >= 60 && (
                            <Clock className="h-3.5 w-3.5" />
                          )}
                          {mentee.days_waiting} days
                        </span>
                        {mentee.returned_to_pool && (
                          <span className="block text-xs text-muted-foreground">
                            since rejoining
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {in_pool.length === 0 && (
                <p className="text-center py-5 text-muted-foreground text-sm">
                  No mentees are waiting in the pool.
                </p>
              )}

              {selected.length > 0 && (
                <div className="mt-4 flex items-center justify-between rounded-md border bg-muted/40 px-4 py-3">
                  <span className="text-sm">
                    {selected.length}{' '}
                    {selected.length === 1 ? 'mentee' : 'mentees'} selected
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="gap-1"
                    onClick={() => setConfirming(true)}
                  >
                    <UserMinus className="h-3.5 w-3.5" />
                    Remove from pool
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="inactive" className="mt-4">
              <DataTable
                data={inactive}
                columns={inactiveColumns}
                emptyMessage="No mentees are currently inactive."
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {selected.length}{' '}
              {selected.length === 1 ? 'mentee' : 'mentees'} from the pool?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will stop appearing to mentors, and each one will be emailed
              to say their application is now inactive and how to rejoin. This
              cannot be undone — you would have to reactivate them individually.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deactivateSelected}
              disabled={submitting}
            >
              {submitting ? 'Removing…' : 'Remove and notify'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
